/**
 * Who changed this record, and what they changed.
 *
 * The movement history beside it comes from the scan ledger and has always been
 * trustworthy. This is the other half: the catalog used to be edited silently,
 * so a spec that turned out wrong had no story behind it. On customer-owned
 * tooling "who changed the cavity count from 24 to 16" is a question that
 * arrives with a lawyer.
 *
 * Online-only. The trail is append-only and unbounded, and a device has no use
 * for it.
 */
import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import type { AuditLogRow } from '@mouldtrack/shared';
import { Card, CardBody, CardHeader, CardTitle, Code } from '../../components/ui/primitives.js';
import { Button } from '../../components/ui/button.js';
import { isNetworkError, listAuditLog } from '../../api/client.js';
import type { MouldRow } from '../../db/queries.js';

/** Field names as they read on the form, not as the column is spelled. */
const FIELD_LABEL: Record<string, string> = {
  assetCode: 'Mould ID',
  name: 'Name',
  catalogNo: 'S.NO',
  cavities: 'Cavities',
  moldSize: 'Mould size',
  manufactureYear: 'Manufacturing year',
  qrNumber: 'QR number',
  toolTransfer: 'Tool transfer',
  manufactureDate: 'Manufacture date',
  ratedLife: 'Rated life',
  expectedBackBy: 'Expected back by',
  photoUrl: 'Photo',
  description: 'Notes',
  isActive: 'Active',
  projectId: 'Project',
  ownerCompanyId: 'Owner',
  customerCompanyId: 'Customer',
  manufactureCompanyId: 'Manufacturer',
  designerId: 'Designer',
  toolTypeId: 'Tool type',
  runnerTypeId: 'Runner type',
  gateTypeId: 'Gate type',
  ejectionTypeId: 'Ejection type',
  coreCavityMaterialId: 'Core / cavity material',
  moldBaseMaterialId: 'Mould base material',
  materialGradeId: 'Material grade',
  mechanismTypeId: 'Sliders / lifters',
};

function value(v: string | null): string {
  if (v === null || v === '') return 'nothing';
  return v;
}

export function MouldAuditTrail({ mould }: { mould: MouldRow }) {
  const mouldId = mould.id;
  const [rows, setRows] = useState<AuditLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listAuditLog(`?entity=moulds&entityId=${encodeURIComponent(mouldId)}&limit=100`)
      .then((r) => !cancelled && setRows(r))
      .catch((e) => {
        if (cancelled) return;
        setRows([]);
        setError(
          isNetworkError(e)
            ? 'No connection. The trail is kept on the server.'
            : // A Viewer or Supervisor has no business reading who changed what.
              'You do not have access to the change history.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, mouldId]);

  return (
    <Card className="mb-4">
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="size-4 text-muted-foreground" aria-hidden />
            Change history
          </CardTitle>
          {/* Stated up front and without a request: it comes off the record
              itself, so it is right for every mould — including the ones
              imported before the trail existed, and the ones the server-side
              importer creates, which never pass through the sync handler that
              writes it. */}
          <p className="mt-0.5 text-sm text-muted-foreground">
            {mould.createdAt
              ? `Added to the system on ${new Date(mould.createdAt).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}`
              : 'Added before the system started recording that'}
          </p>
        </div>
        {/* Loaded on demand: most visits to a mould record are not
            investigations, and this is an extra request every time. */}
        <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </CardHeader>

      {open ? (
        <CardBody>
          {error ? (
            <p className="text-sm text-muted-foreground">{error}</p>
          ) : rows === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing recorded yet. Changes made from now on appear here - who, what, and when.
            </p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {rows.map((r) => (
                <li key={r.id} className="text-sm">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">{r.actor?.name ?? 'Someone'}</span>
                    <span className="text-muted-foreground">
                      {r.action === 'create'
                        ? 'created this record'
                        : r.action === 'delete'
                          ? 'deleted this record'
                          : `changed ${FIELD_LABEL[r.field ?? ''] ?? r.field}`}
                    </span>
                    <span className="ml-auto text-xs text-subtle-foreground">
                      {new Date(r.at).toLocaleString()}
                    </span>
                  </div>
                  {r.action === 'update' ? (
                    <div className="mt-0.5 flex flex-wrap items-baseline gap-1.5 text-xs">
                      <Code className="text-xs">{value(r.before)}</Code>
                      <span className="text-muted-foreground">→</span>
                      <Code className="text-xs">{value(r.after)}</Code>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      ) : null}
    </Card>
  );
}
